import { useEffect, useState, useCallback, useRef } from "react";
import {
  Wrench, BookOpen, ClipboardCheck, FileText, Plus, Trash2, Upload, Loader2,
  RefreshCw, Save, ArrowLeft, Search, Download, X, Eye, Camera, Image as ImageIcon, Printer,
  Hammer, CheckCircle2, AlertCircle,
} from "lucide-react";
import { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { downloadRepairEstimatePdf, downloadInspectionPdf } from "../lib/exporters";

const fmtMoney = (n, ccy = "USD") =>
  `${ccy} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");

const STATUS_COLORS = {
  DRAFT:       "ps-pill-warn",
  SENT:        "ps-pill-info",
  APPROVED:    "ps-pill-success",
  IN_PROGRESS: "ps-pill-warn",
  COMPLETED:   "ps-pill-info",
  REJECTED:    "ps-pill-danger",
  INVOICED:    "ps-pill-success",
};
const STATUS_LABELS = {
  DRAFT:       "Draft",
  SENT:        "Sent to customer",
  APPROVED:    "Approved",
  IN_PROGRESS: "In progress",
  COMPLETED:   "Ready to invoice",
  REJECTED:    "Rejected",
  INVOICED:    "Invoiced",
};

// ─────────────────────────────────────── CATALOG TAB ───────────────────────────────────────
function CatalogTab({ canEdit, canDelete }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [markup, setMarkup] = useState(30);
  const [replace, setReplace] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingParts, setUploadingParts] = useState(false);
  const [uploadingPelican, setUploadingPelican] = useState(false);
  const fileRef = useRef(null);
  const partsFileRef = useRef(null);
  const pelicanFileRef = useRef(null);
  const [editing, setEditing] = useState(null); // null = none, "new" = new row, or item object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? `?q=${encodeURIComponent(search)}` : "";
      const { data } = await api.get(`/repairs/catalog${params}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/repairs/catalog/upload?markup_pct=${markup}&replace=${replace}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`Imported ${data.inserted} items (skipped ${data.skipped}) — markup ${data.markup_pct}%`);
      e.target.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const handlePartsUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingParts(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/repairs/catalog/upload-parts-pdf?markup_pct=${markup}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`${data.vendor}: imported ${data.lines} parts from offer ${data.offer_number || "—"} (€→USD ${data.eur_to_usd?.toFixed(3)} · markup ${data.markup_pct}%)`);
      e.target.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingParts(false); }
  };

  const handlePelicanUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPelican(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/repairs/catalog/upload-pelican-pdf?markup_pct=${markup}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`${data.vendor}: imported ${data.lines} parts from order ${data.offer_number || "—"} (€${data.subtotal_eur?.toFixed(2)} → USD @ ${data.eur_to_usd?.toFixed(3)} · markup ${data.markup_pct}%)`);
      e.target.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingPelican(false); }
  };

  const save = async (item) => {
    try {
      await api.post("/repairs/catalog", item);
      toast.success(`${item.item_code} saved`);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete catalog item ${code}?`)) return;
    try {
      await api.delete(`/repairs/catalog/${code}`);
      toast.success(`Deleted ${code}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="border border-blue-100 bg-blue-50/50 p-4 text-xs text-gray-700">
        <strong className="block uppercase tracking-[0.16em] text-[#006E9E] mb-1">
          Repair rate card · ITCO-style codes
        </strong>
        Upload <b>XLSX</b> for generic rate lists, a <b>Pelican Commercial Invoice PDF</b>, or a <b>Van Doorn OFFERTE PDF</b> for parts.
        Markup % is applied on import (e.g. 30%). Pelican / Van Doorn quotes are auto-converted from EUR to USD using your latest FX rate.
      </div>

      <div className="border border-gray-200 bg-white p-4 grid sm:grid-cols-6 gap-3 items-end">
        <div className="sm:col-span-2">
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Search</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="item code or description"
                   className="ps-input pl-8 font-mono" data-testid="catalog-search" />
          </div>
        </div>
        {canEdit && (
          <>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Markup % on upload</label>
              <input type="number" value={markup} min={0} max={200} onChange={(e) => setMarkup(Number(e.target.value))}
                     className="ps-input font-mono" data-testid="catalog-markup" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Replace mode</label>
              <label className="ps-input inline-flex items-center gap-2 font-mono cursor-pointer">
                <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="catalog-replace" />
                Wipe before
              </label>
            </div>
            <div className="flex flex-wrap gap-2 sm:col-span-2">
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} data-testid="catalog-file-input" />
              <input ref={partsFileRef} type="file" accept=".pdf" className="hidden" onChange={handlePartsUpload} data-testid="catalog-parts-pdf-input" />
              <input ref={pelicanFileRef} type="file" accept=".pdf" className="hidden" onChange={handlePelicanUpload} data-testid="catalog-pelican-pdf-input" />
              <button className="ps-btn ps-btn-secondary flex-1" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="catalog-upload-btn">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> XLSX</>}
              </button>
              <button className="ps-btn ps-btn-secondary flex-1" onClick={() => pelicanFileRef.current?.click()} disabled={uploadingPelican} data-testid="catalog-pelican-pdf-btn">
                {uploadingPelican ? <><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</> : <><Upload className="w-4 h-4" /> PDF (Pelican)</>}
              </button>
              <button className="ps-btn ps-btn-secondary flex-1" onClick={() => partsFileRef.current?.click()} disabled={uploadingParts} data-testid="catalog-parts-pdf-btn">
                {uploadingParts ? <><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</> : <><Upload className="w-4 h-4" /> PDF (Van Doorn)</>}
              </button>
              <button className="ps-btn ps-btn-primary" onClick={() => setEditing({ item_code: "", description: "", default_labor_hours: 0, material_cost: 0, currency: "USD" })} data-testid="catalog-add">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          </>
        )}
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="ps-table" data-testid="catalog-table">
          <thead>
            <tr>
              <th>Item Code</th>
              <th>Description</th>
              <th>Vendor</th>
              <th>Unit</th>
              <th>Damage</th>
              <th>Location</th>
              <th className="text-right">Labor hrs</th>
              <th className="text-right">Material</th>
              <th>Currency</th>
              {canEdit && <th></th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={canEdit ? 10 : 9} className="text-center py-10 text-gray-400">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={canEdit ? 10 : 9} className="text-center py-10 text-gray-400">
                No catalog items. Upload an XLSX/PDF or click <strong>Add</strong>.
              </td></tr>
            )}
            {!loading && items.map((it) => (
              <tr key={it.item_code} data-testid={`catalog-row-${it.item_code}`}>
                <td className="font-mono font-bold">{it.item_code}</td>
                <td className="text-xs">{it.description}</td>
                <td className="font-mono text-[10px] uppercase tracking-[0.12em] text-gray-600">{it.vendor || "—"}</td>
                <td className="font-mono text-xs text-gray-600">{it.unit || "—"}</td>
                <td className="font-mono text-xs text-gray-600">{it.damage_code || "—"}</td>
                <td className="font-mono text-xs text-gray-600">{it.location || "—"}</td>
                <td className="text-right font-mono">{Number(it.default_labor_hours || 0).toFixed(2)}</td>
                <td className="text-right font-mono font-bold">{fmtMoney(it.material_cost, it.currency)}</td>
                <td className="font-mono text-xs">{it.currency}</td>
                {canEdit && (
                  <td className="text-right">
                    <button className="ps-btn ps-btn-secondary h-7 px-2 text-xs" onClick={() => setEditing(it)} data-testid={`catalog-edit-${it.item_code}`}>Edit</button>
                    {canDelete && (
                      <button className="ps-btn ps-btn-secondary h-7 px-2 text-xs text-red-600 ml-1"
                              onClick={() => remove(it.item_code)} data-testid={`catalog-del-${it.item_code}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEditing(null)}>
          <div className="bg-white border border-gray-200 max-w-xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-700">
                {editing.id ? "Edit catalog item" : "New catalog item"}
              </div>
              <button onClick={() => setEditing(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              {[
                ["item_code", "Item Code", "text"],
                ["description", "Description", "text", "col-span-2"],
                ["damage_code", "Damage code", "text"],
                ["location", "Location", "text"],
                ["default_labor_hours", "Labor hours", "number"],
                ["material_cost", "Material cost", "number"],
                ["currency", "Currency", "text"],
              ].map(([k, l, t, cls]) => (
                <div key={k} className={cls || ""}>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">{l}</label>
                  <input type={t} value={editing[k] ?? ""} onChange={(e) => setEditing({ ...editing, [k]: t === "number" ? Number(e.target.value) : e.target.value })} className="ps-input font-mono" data-testid={`catalog-form-${k}`} />
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button className="ps-btn ps-btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="ps-btn ps-btn-primary" onClick={() => save(editing)} data-testid="catalog-save"><Save className="w-4 h-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────── ESTIMATES TAB ───────────────────────────────────────
function EstimatesTab({ canEdit, canDelete, canInvoice }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [building, setBuilding] = useState(null); // null | "new"
  const [catalog, setCatalog] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/repairs/estimates");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/repairs/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const setStatus = async (eid, status) => {
    try {
      await api.patch(`/repairs/estimates/${eid}/status?status=${status}`);
      toast.success(`Estimate set to ${status}`);
      load();
      if (selected) {
        const { data } = await api.get(`/repairs/estimates/${eid}`);
        setSelected(data);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const convertToInvoice = async (eid) => {
    if (!window.confirm("Convert this COMPLETED repair to an invoice? The estimate will be locked as INVOICED.")) return;
    try {
      const { data } = await api.post(`/repairs/estimates/${eid}/convert-to-invoice`);
      toast.success(`Invoice ${data.invoice_no} created — ${data.currency} ${Number(data.totals.grand_total).toFixed(2)}`);
      load();
      const { data: refreshed } = await api.get(`/repairs/estimates/${eid}`);
      setSelected(refreshed);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (eid, no) => {
    if (!window.confirm(`Delete estimate ${no}?`)) return;
    try {
      await api.delete(`/repairs/estimates/${eid}`);
      toast.success(`Deleted ${no}`);
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const editEstimate = async (eid, patch) => {
    try {
      // An empty patch is also used as a "please refresh me" hook from child modals.
      const { data } = patch && Object.keys(patch).length
        ? await api.put(`/repairs/estimates/${eid}`, patch)
        : await api.get(`/repairs/estimates/${eid}`);
      if (patch && Object.keys(patch).length) {
        toast.success(`Estimate ${data.estimate_no} updated — ${data.currency} ${Number(data.totals.grand_total).toFixed(2)}`);
      }
      load();
      setSelected(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  if (building === "new") {
    return <EstimateBuilder catalog={catalog} onCancel={() => setBuilding(null)} onSaved={(d) => { setBuilding(null); load(); setSelected(d); }} />;
  }
  if (selected) {
    return <EstimateDetail estimate={selected} canEdit={canEdit} canDelete={canDelete} canInvoice={canInvoice}
      isAdmin={canDelete}
      onBack={() => setSelected(null)} onStatus={setStatus} onDelete={remove} onConvert={convertToInvoice} onEdit={editEstimate} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-gray-500 font-mono">{items.length} estimate{items.length === 1 ? "" : "s"}</div>
        <div className="flex gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={load} data-testid="estimates-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
          {canEdit && (
            <button className="ps-btn ps-btn-primary" onClick={() => setBuilding("new")} data-testid="estimate-new">
              <Plus className="w-4 h-4" /> New Estimate
            </button>
          )}
        </div>
      </div>

      {/* KPI strip — pipeline by status */}
      <div className="grid grid-cols-3 sm:grid-cols-7 border border-gray-200 bg-white divide-x divide-gray-200" data-testid="estimates-kpis">
        {[
          { k: "DRAFT",       l: "Drafts",       cls: "text-amber-600" },
          { k: "SENT",        l: "Sent",         cls: "text-blue-600" },
          { k: "APPROVED",    l: "Approved",     cls: "text-emerald-600" },
          { k: "IN_PROGRESS", l: "In progress",  cls: "text-amber-700" },
          { k: "COMPLETED",   l: "Ready bill",   cls: "text-[#006E9E]" },
          { k: "INVOICED",    l: "Invoiced",     cls: "text-emerald-700" },
          { k: "REJECTED",    l: "Rejected",     cls: "text-red-600" },
        ].map(({ k, l, cls }) => {
          const n = items.filter((x) => x.status === k).length;
          return (
            <div key={k} className="p-4" data-testid={`kpi-${k.toLowerCase()}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{l}</div>
              <div className={`font-mono text-2xl font-black mt-1 ${cls}`}>{n}</div>
            </div>
          );
        })}
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="ps-table" data-testid="estimates-table">
          <thead>
            <tr>
              <th>Estimate No</th>
              <th>Container</th>
              <th>Customer</th>
              <th className="text-right">Lines</th>
              <th className="text-right">Labor</th>
              <th className="text-right">Material</th>
              <th className="text-right">Total</th>
              <th>Status</th>
              <th>Issued</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center py-10 text-gray-400">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-gray-400">No estimates yet.</td></tr>
            )}
            {!loading && items.map((e) => {
              const t = e.totals || {};
              return (
                <tr key={e.id} data-testid={`estimate-row-${e.estimate_no}`}>
                  <td className="font-mono font-bold">{e.estimate_no}</td>
                  <td className="font-mono">{e.container_number}</td>
                  <td className="text-xs">{e.customer || "—"}</td>
                  <td className="text-right font-mono">{t.line_items ?? 0}</td>
                  <td className="text-right font-mono">{fmtMoney(t.total_labor, e.currency)}</td>
                  <td className="text-right font-mono">{fmtMoney(t.total_material, e.currency)}</td>
                  <td className="text-right font-mono font-bold text-[#006E9E]">{fmtMoney(t.grand_total, e.currency)}</td>
                  <td><span className={`ps-pill ${STATUS_COLORS[e.status] || "ps-pill-info"}`}><span className="dot" /> {STATUS_LABELS[e.status] || e.status}</span></td>
                  <td className="text-xs font-mono text-gray-500">{fmtDate(e.created_at)}</td>
                  <td className="text-right">
                    <button className="ps-btn ps-btn-secondary h-7 px-3 text-xs" onClick={() => setSelected(e)} data-testid={`open-estimate-${e.estimate_no}`}>
                      <Eye className="w-3.5 h-3.5" /> View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EstimateBuilder({ catalog, onCancel, onSaved }) {
  const [container, setContainer] = useState("");
  const [customer, setCustomer] = useState("");
  const [laborRate, setLaborRate] = useState(60);
  const [lines, setLines] = useState([]);
  const [picker, setPicker] = useState("");
  const [saving, setSaving] = useState(false);
  // Yard-validation state for the Container No autocomplete
  const [yardMatches, setYardMatches] = useState([]);   // [{container_number, iso_type, last_carrier?}]
  const [yardOpen, setYardOpen] = useState(false);
  const [yardValid, setYardValid] = useState(null);     // null = not checked, true = in yard, false = not found
  const [yardCheckedTank, setYardCheckedTank] = useState(null);
  // Customer dropdown source — all partners (carrier + customer + leaseholder
  // roles) so repairs can be billed to whoever owns the tank. Prevents free-
  // text drift like "Stolt Tank Containers" vs "Stolt Tank Containers B.V.".
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    api.get("/partners?fields=code,legal_name")
      .then((r) => setPartners(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPartners([]));
  }, []);

  // Live in-yard suggest as user types — minimum 2 chars, debounced
  useEffect(() => {
    const q = (container || "").trim();
    if (q.length < 2) { setYardMatches([]); setYardOpen(false); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/containers?in_depot=true&q=${encodeURIComponent(q)}&limit=8`);
        setYardMatches(data || []);
        setYardOpen(true);
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [container]);

  // Whole-tank confirmation lookup — only when input looks ISO-6346 valid
  // (4 letters + 6-7 digits). Fires whenever the user pauses typing on a
  // complete tank number, so we can show a green ✓ or amber warning.
  useEffect(() => {
    const v = (container || "").trim().toUpperCase();
    if (!/^[A-Z]{4}\d{6,7}$/.test(v)) {
      setYardValid(null); setYardCheckedTank(null);
      return undefined;
    }
    if (yardCheckedTank === v) return undefined;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/containers?q=${encodeURIComponent(v)}&limit=1`);
        const hit = (data || []).find((c) => c.container_number === v);
        setYardCheckedTank(v);
        if (!hit) { setYardValid(false); return; }
        setYardValid(hit.last_status === "IN");
      } catch { setYardValid(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [container, yardCheckedTank]);

  const pickYardTank = (c) => {
    setContainer(c.container_number);
    setYardOpen(false);
    setYardValid(true);
    setYardCheckedTank(c.container_number);
    // Auto-fill customer from the container's denormalised owner if empty.
    // Never overwrite — operators may be billing a leaseholder instead of
    // the day-to-day customer, so we respect their pre-existing input.
    if (!customer && c.customer) setCustomer(c.customer);
  };

  const addFromCatalog = (code) => {
    const it = catalog.find((c) => c.item_code === code);
    if (!it) return;
    setLines((prev) => [...prev, {
      item_code: it.item_code,
      description: it.description,
      damage_code: it.damage_code || "",
      location: it.location || "",
      qty: 1,
      labor_hours: it.default_labor_hours || 0,
      labor_rate: laborRate,
      material_cost: it.material_cost || 0,
    }]);
    setPicker("");
  };

  const addBlank = () => setLines((prev) => [...prev, { item_code: "", description: "", damage_code: "", location: "", qty: 1, labor_hours: 0, labor_rate: laborRate, material_cost: 0 }]);
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));
  const updateLine = (i, key, val) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, [key]: val } : l));

  const totals = lines.reduce((acc, l) => {
    const lab = Number(l.qty || 1) * Number(l.labor_hours || 0) * Number(l.labor_rate || laborRate);
    const mat = Number(l.qty || 1) * Number(l.material_cost || 0);
    return { lab: acc.lab + lab, mat: acc.mat + mat };
  }, { lab: 0, mat: 0 });

  const save = async () => {
    const tank = container.trim().toUpperCase();
    if (!tank) { toast.error("Container number is required"); return; }
    if (!/^[A-Z]{4}\d{6,7}$/.test(tank)) {
      toast.error("Container number must be 4 letters + 6-7 digits (e.g. MEBU1266881)");
      return;
    }
    if (yardValid === false) {
      toast.error(`${tank} is not currently in the yard — please pick a tank from the suggestions or gate it in first.`);
      return;
    }
    if (!lines.length) { toast.error("Add at least one line"); return; }
    // Reject pure-numeric item codes (e.g. "001", "002") — force users to pick
    // from the catalog or leave the code blank.
    const bad = lines.findIndex((l) => {
      const c = (l.item_code || "").trim();
      if (!c) return false;
      return /^\d+$/.test(c);
    });
    if (bad >= 0) {
      toast.error(`Line ${bad + 1}: item code can't be just a number — pick one from the catalog (e.g. OVPL-200) or leave blank.`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/repairs/estimates", {
        container_number: tank,
        customer: customer || null,
        currency: "USD",
        labor_rate: Number(laborRate),
        lines,
      });
      toast.success(`Estimate ${data.estimate_no} created — ${fmtMoney(data.totals.grand_total)}`);
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // Split catalog into OPERATIONS (labour) and PARTS for the dropdown.
  const operations = (catalog || []).filter((c) => (c.kind || "PART") === "OPERATION");
  const parts = (catalog || []).filter((c) => (c.kind || "PART") === "PART");

  return (
    <div className="space-y-4" data-testid="estimate-builder">
      <div className="flex items-center justify-between">
        <button className="ps-btn ps-btn-secondary" onClick={onCancel}><ArrowLeft className="w-4 h-4" /> Back</button>
        <button className="ps-btn ps-btn-primary" onClick={save} disabled={saving} data-testid="estimate-save">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save Estimate</>}
        </button>
      </div>

      <div className="border border-gray-200 bg-white p-4 grid sm:grid-cols-4 gap-3">
        <div className="relative">
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
            Container No <span className="text-gray-400 normal-case">· must be in yard</span>
          </label>
          <input
            value={container}
            onChange={(e) => setContainer(e.target.value.toUpperCase())}
            onFocus={() => { if (yardMatches.length) setYardOpen(true); }}
            onBlur={() => setTimeout(() => setYardOpen(false), 150)}
            className={`ps-input font-mono pr-8 ${
              yardValid === true ? "border-emerald-500" :
              yardValid === false ? "border-red-400 bg-red-50" : ""
            }`}
            placeholder="MEBU1266881"
            data-testid="estimate-container"
            autoComplete="off"
          />
          {yardValid === true && (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 absolute right-2 top-[30px]" data-testid="container-in-yard" />
          )}
          {yardValid === false && (
            <X className="w-4 h-4 text-red-500 absolute right-2 top-[30px]" data-testid="container-not-in-yard" />
          )}
          {yardValid === false && (
            <div className="text-[11px] text-red-600 mt-1" data-testid="container-not-in-yard-msg">
              Tank not currently in the yard — typo? Or gate it in first.
            </div>
          )}
          {yardOpen && yardMatches.length > 0 && (
            <ul
              className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-auto border border-gray-200 bg-white shadow-md text-xs"
              data-testid="container-suggest"
            >
              {yardMatches.map((c) => (
                <li
                  key={c.container_number}
                  onMouseDown={(ev) => { ev.preventDefault(); pickYardTank(c); }}
                  className="px-3 py-1.5 hover:bg-blue-50 cursor-pointer flex items-center justify-between gap-3"
                  data-testid={`container-suggest-${c.container_number}`}
                >
                  <span className="font-mono font-bold">{c.container_number}</span>
                  <span className="text-[10px] uppercase tracking-wider text-gray-500">
                    {c.iso_type || "—"}{c.last_carrier ? ` · ${c.last_carrier}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
            Customer <span className="text-gray-400 normal-case">· required</span>
          </label>
          <select
            required
            value={partners.find((p) => (p.legal_name || p.code) === customer)?.code || ""}
            onChange={(e) => {
              const code = e.target.value;
              const partner = partners.find((p) => p.code === code);
              setCustomer(partner ? (partner.legal_name || partner.code) : "");
            }}
            className="ps-input"
            data-testid="estimate-customer"
          >
            <option value="">— Select customer —</option>
            {partners.map((p) => (
              <option key={p.code} value={p.code}>
                {p.code}{p.legal_name && p.legal_name !== p.code ? ` · ${p.legal_name}` : ""}
              </option>
            ))}
          </select>
          {!customer && (
            <div className="text-[11px] text-gray-500 mt-1">
              Can't find them? <a href="/partners?new=1" target="_blank" rel="noreferrer" className="underline">Create in Partners</a> first.
            </div>
          )}
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Hour Tariff (USD/hr)</label>
          <input type="number" value={laborRate} onChange={(e) => setLaborRate(Number(e.target.value))} className="ps-input font-mono" data-testid="estimate-rate" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Add from catalog</label>
          <select value={picker} onChange={(e) => addFromCatalog(e.target.value)} className="ps-input font-mono" data-testid="estimate-picker">
            <option value="">— select item —</option>
            {operations.length > 0 && (
              <optgroup label={`Operations (${operations.length})`}>
                {operations.map((c) => (
                  <option key={c.item_code} value={c.item_code}>
                    {c.item_code} — {c.description.slice(0, 55)} · {c.default_labor_hours || 0}h
                  </option>
                ))}
              </optgroup>
            )}
            {parts.length > 0 && (
              <optgroup label={`Parts (${parts.length})`}>
                {parts.map((c) => (
                  <option key={c.item_code} value={c.item_code}>
                    {c.item_code} — {c.description.slice(0, 55)} (${c.material_cost})
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="ps-table">
          <thead>
            <tr>
              <th></th>
              <th>Item Code</th>
              <th>Description</th>
              <th>Dam</th>
              <th>Loc</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Labor hrs</th>
              <th className="text-right">Rate</th>
              <th className="text-right">Material</th>
              <th className="text-right">Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr><td colSpan={11} className="text-center py-8 text-gray-400 text-sm">Add lines from catalog or click the + below.</td></tr>
            )}
            {lines.map((l, i) => {
              const lab = Number(l.qty || 1) * Number(l.labor_hours || 0) * Number(l.labor_rate || laborRate);
              const mat = Number(l.qty || 1) * Number(l.material_cost || 0);
              const total = lab + mat;
              return (
                <tr key={i} data-testid={`estimate-line-${i}`}>
                  <td className="text-xs font-mono text-gray-400">{i + 1}</td>
                  <td><input
                    className={`ps-input h-7 px-2 font-mono text-xs w-24 ${
                      l.item_code && /^\d+$/.test(l.item_code.trim())
                        ? "border-red-400 bg-red-50"
                        : ""
                    }`}
                    value={l.item_code}
                    onChange={(e) => updateLine(i, "item_code", e.target.value)}
                    placeholder="e.g. OVPL-200"
                    title={l.item_code && /^\d+$/.test(l.item_code.trim())
                      ? "Numeric-only codes (001, 002…) are not allowed. Pick from the catalog or leave blank."
                      : ""}
                  /></td>
                  <td><input className="ps-input h-7 px-2 text-xs w-full" value={l.description} onChange={(e) => updateLine(i, "description", e.target.value)} /></td>
                  <td><input className="ps-input h-7 px-2 font-mono text-xs w-14 uppercase" value={l.damage_code} onChange={(e) => updateLine(i, "damage_code", e.target.value.toUpperCase())} /></td>
                  <td><input className="ps-input h-7 px-2 font-mono text-xs w-16 uppercase" value={l.location} onChange={(e) => updateLine(i, "location", e.target.value.toUpperCase())} /></td>
                  <td><input type="number" className="ps-input h-7 px-2 text-right font-mono text-xs w-16" value={l.qty} onChange={(e) => updateLine(i, "qty", Number(e.target.value))} /></td>
                  <td><input type="number" step="0.1" className="ps-input h-7 px-2 text-right font-mono text-xs w-20" value={l.labor_hours} onChange={(e) => updateLine(i, "labor_hours", Number(e.target.value))} /></td>
                  <td><input type="number" step="0.01" className="ps-input h-7 px-2 text-right font-mono text-xs w-20" value={l.labor_rate} onChange={(e) => updateLine(i, "labor_rate", Number(e.target.value))} /></td>
                  <td><input type="number" step="0.01" className="ps-input h-7 px-2 text-right font-mono text-xs w-24" value={l.material_cost} onChange={(e) => updateLine(i, "material_cost", Number(e.target.value))} /></td>
                  <td className="text-right font-mono font-bold">{fmtMoney(total)}</td>
                  <td><button className="text-red-600 hover:bg-red-50 p-1" onClick={() => removeLine(i)}><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              );
            })}
            <tr>
              <td colSpan={11}>
                <button className="text-xs text-[#006E9E] font-bold inline-flex items-center gap-1 px-3 py-2 hover:bg-blue-50" onClick={addBlank} data-testid="estimate-add-blank">
                  <Plus className="w-3.5 h-3.5" /> Add blank line
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="border border-gray-200 bg-white p-5 grid sm:grid-cols-4 divide-x divide-gray-200">
        <div><div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Lines</div><div className="font-mono text-2xl font-black">{lines.length}</div></div>
        <div className="pl-5"><div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Total labor</div><div className="font-mono text-2xl font-black">{fmtMoney(totals.lab)}</div></div>
        <div className="pl-5"><div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Total material</div><div className="font-mono text-2xl font-black">{fmtMoney(totals.mat)}</div></div>
        <div className="pl-5"><div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Grand total</div><div className="font-mono text-2xl font-black text-[#006E9E]">{fmtMoney(totals.lab + totals.mat)}</div></div>
      </div>
    </div>
  );
}

function EstimateDetail({ estimate, canEdit, canDelete, canInvoice, isAdmin, onBack, onStatus, onDelete, onConvert, onEdit }) {
  const t = estimate.totals || {};
  const [editing, setEditing] = useState(false);
  const [woModal, setWoModal] = useState(false);
  const [convertModal, setConvertModal] = useState(false);
  const editable = ["DRAFT", "SENT", "APPROVED"].includes(estimate.status);
  const cwo = estimate.customer_work_order || {};
  const hasWo = !!(cwo.wo_no || "").trim();
  return (
    <div className="space-y-4" data-testid="estimate-detail">
      <div className="flex items-center justify-between">
        <button className="ps-btn ps-btn-secondary" onClick={onBack}><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="flex gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={() => downloadRepairEstimatePdf(estimate)} data-testid="estimate-pdf">
            <Printer className="w-4 h-4" /> Print PDF
          </button>
          {canEdit && (editable || isAdmin) && (
            <button className="ps-btn ps-btn-secondary" onClick={() => setEditing(true)} data-testid="estimate-edit" title={editable ? "Edit lines / labour rate" : `Late amendment (admin) — status is ${estimate.status}`}>
              <Wrench className="w-4 h-4" /> Edit / Discount{!editable && isAdmin ? " (Late)" : ""}
            </button>
          )}
          {canEdit && estimate.status === "DRAFT" && (
            <button className="ps-btn ps-btn-secondary" onClick={() => onStatus(estimate.id, "SENT")} data-testid="estimate-send">Mark Sent</button>
          )}
          {canEdit && estimate.status === "SENT" && (
            <>
              <button className="ps-btn ps-btn-primary" onClick={() => onStatus(estimate.id, "APPROVED")} data-testid="estimate-approve">Approve</button>
              <button className="ps-btn ps-btn-secondary text-red-600" onClick={() => onStatus(estimate.id, "REJECTED")} data-testid="estimate-reject">Reject</button>
            </>
          )}
          {canEdit && estimate.status === "APPROVED" && (
            <>
              <button className="ps-btn ps-btn-primary" onClick={() => onStatus(estimate.id, "IN_PROGRESS")} data-testid="estimate-start">
                <Hammer className="w-4 h-4" /> Start Repair
              </button>
              <button className="ps-btn ps-btn-secondary text-red-600" onClick={() => onStatus(estimate.id, "REJECTED")} data-testid="estimate-reject">Reject</button>
            </>
          )}
          {canEdit && estimate.status === "IN_PROGRESS" && (
            <>
              <button className="ps-btn ps-btn-primary" onClick={() => onStatus(estimate.id, "COMPLETED")} data-testid="estimate-complete">
                <CheckCircle2 className="w-4 h-4" /> Mark Complete
              </button>
              <button className="ps-btn ps-btn-secondary" onClick={() => onStatus(estimate.id, "APPROVED")} data-testid="estimate-revert" title="Move back to Approved (pause)">
                <ArrowLeft className="w-4 h-4" /> Pause
              </button>
            </>
          )}
          {(canEdit || canInvoice) && (
            ["IN_PROGRESS", "COMPLETED"].includes(estimate.status)
            || (estimate.status === "INVOICED" && isAdmin)
          ) && (
            <button
              className={`ps-btn ${hasWo ? "ps-btn-secondary" : "ps-btn-secondary border-amber-400 text-amber-700"}`}
              onClick={() => setWoModal(true)}
              data-testid="estimate-work-order"
              title={
                estimate.status === "INVOICED"
                  ? "Late amendment — edit work order on invoiced estimate (admin)"
                  : (hasWo ? "Edit customer work order" : "Add customer work order (required for invoicing)")
              }
            >
              <ClipboardCheck className="w-4 h-4" />
              {hasWo ? `WO ${cwo.wo_no}` : (estimate.status === "INVOICED" ? "Add WO (late)" : "Add Work Order")}
            </button>
          )}
          {canInvoice && estimate.status === "COMPLETED" && (
            <>
              <button
                className="ps-btn ps-btn-primary"
                onClick={() => hasWo ? setConvertModal(true) : setWoModal(true)}
                data-testid="estimate-convert"
                title={hasWo ? "Generate the customer invoice" : "Customer Work Order required first"}
              >
                <FileText className="w-4 h-4" /> Generate Invoice
              </button>
              {canEdit && (
                <button className="ps-btn ps-btn-secondary" onClick={() => onStatus(estimate.id, "IN_PROGRESS")} data-testid="estimate-reopen" title="Reopen the repair">
                  <ArrowLeft className="w-4 h-4" /> Reopen Repair
                </button>
              )}
            </>
          )}
          {canEdit && estimate.status === "REJECTED" && (
            <button className="ps-btn ps-btn-secondary" onClick={() => onStatus(estimate.id, "DRAFT")} data-testid="estimate-revive" title="Revive as a new draft">
              Revive to Draft
            </button>
          )}
          {estimate.status === "INVOICED" && estimate.invoice_no && (
            <a
              className="ps-btn ps-btn-secondary"
              href={`/invoices`}
              data-testid="estimate-view-invoice"
              title={`Linked to invoice ${estimate.invoice_no}`}
            >
              <FileText className="w-4 h-4" /> View {estimate.invoice_no}
            </a>
          )}
          {canDelete && (
            <button className="ps-btn ps-btn-secondary text-red-600" onClick={() => onDelete(estimate.id, estimate.estimate_no)} data-testid="estimate-delete">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="border border-gray-200 bg-white">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Repair estimate</div>
            <div className="font-mono font-bold text-lg">{estimate.estimate_no}</div>
            <div className="text-xs text-gray-500 mt-1">{estimate.container_number} · {estimate.customer || "—"}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Grand total</div>
            <div className="font-mono font-black text-3xl text-[#006E9E]">{fmtMoney(t.grand_total, estimate.currency)}</div>
            <div className="mt-2"><span className={`ps-pill ${STATUS_COLORS[estimate.status] || "ps-pill-info"}`}><span className="dot" /> {STATUS_LABELS[estimate.status] || estimate.status}</span></div>
          </div>
        </div>
        <div className="grid grid-cols-4 divide-x divide-gray-200">
          <div className="p-4"><div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Lines</div><div className="font-mono text-xl font-bold mt-1">{t.line_items ?? 0}</div></div>
          <div className="p-4"><div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Total labor</div><div className="font-mono text-xl font-bold mt-1">{fmtMoney(t.total_labor, estimate.currency)}</div></div>
          <div className="p-4"><div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Total material</div><div className="font-mono text-xl font-bold mt-1">{fmtMoney(t.total_material, estimate.currency)}</div></div>
          <div className="p-4"><div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Hour tariff</div><div className="font-mono text-xl font-bold mt-1">{fmtMoney(estimate.labor_rate, estimate.currency)}</div></div>
        </div>
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="ps-table">
          <thead>
            <tr>
              <th>#</th><th>Item Code</th><th>Description</th><th>Dam</th><th>Loc</th>
              <th className="text-right">Qty</th><th className="text-right">Hrs</th>
              <th className="text-right">Rate/h</th>
              <th className="text-right">Labour</th>
              <th className="text-right">Material</th>
              <th className="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {(estimate.lines || []).map((l, i) => {
              const qty = Number(l.qty || 1);
              const hrs = Number(l.labor_hours || 0);
              const rate = Number(l.labor_rate || 0);
              const matEach = Number(l.material_cost || 0);
              const labSub = qty * hrs * rate;
              const matSub = qty * matEach;
              const hrsDisplay = Number.isInteger(hrs) ? hrs.toFixed(1) : hrs.toFixed(2);
              return (
                <tr key={i}>
                  <td className="font-mono text-xs text-gray-400">{i + 1}</td>
                  <td className="font-mono font-bold text-xs">{l.item_code || "—"}</td>
                  <td className="text-xs">{l.description}</td>
                  <td className="font-mono text-xs">{l.damage_code || "—"}</td>
                  <td className="font-mono text-xs">{l.location || "—"}</td>
                  <td className="text-right font-mono">{qty}</td>
                  <td className="text-right font-mono">{hrsDisplay}</td>
                  <td className="text-right font-mono">{fmtMoney(rate, estimate.currency)}</td>
                  <td className="text-right font-mono">{fmtMoney(labSub, estimate.currency)}</td>
                  <td className="text-right font-mono">{fmtMoney(matSub, estimate.currency)}</td>
                  <td className="text-right font-mono font-bold">{fmtMoney(l.total ?? (labSub + matSub), estimate.currency)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {estimate.discount_reason && (
        <div className="border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 flex items-start gap-2" data-testid="discount-reason">
          <Wrench className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <span className="font-bold uppercase tracking-wider mr-2">Discount on file:</span>
            {estimate.discount_reason}
          </div>
        </div>
      )}

      {hasWo && (
        <div className="border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs flex items-start gap-2" data-testid="customer-work-order-banner">
          <ClipboardCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-700" />
          <div className="flex-1">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="font-bold uppercase tracking-wider text-emerald-900">Customer Work Order:</span>
              <span className="font-mono font-bold text-emerald-900">{cwo.wo_no}</span>
              {cwo.wo_date && <span className="text-emerald-800">dated {new Date(cwo.wo_date).toLocaleDateString()}</span>}
              {cwo.authorised_by && <span className="text-emerald-800">by {cwo.authorised_by}</span>}
              {cwo.file?.url && (
                <a
                  href={(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + cwo.file.url}
                  target="_blank" rel="noreferrer"
                  className="text-emerald-700 underline font-bold"
                  data-testid="work-order-file-link"
                >
                  view document
                </a>
              )}
            </div>
            {cwo.notes && <div className="text-emerald-800 mt-1">{cwo.notes}</div>}
          </div>
        </div>
      )}

      <EstimatePhotosSection estimate={estimate} canEdit={canEdit} isAdmin={isAdmin} />

      {editing && (
        <EstimateEditModal
          estimate={estimate}
          onClose={() => setEditing(false)}
          onSave={async (patch) => { await onEdit(estimate.id, patch); setEditing(false); }}
        />
      )}

      {woModal && (
        <WorkOrderModal
          estimate={estimate}
          onClose={() => setWoModal(false)}
          onSaved={() => { setWoModal(false); /* reload by triggering an empty edit refetch */ onEdit && onEdit(estimate.id, {}); }}
        />
      )}

      {convertModal && (
        <ConvertConfirmModal
          estimate={estimate}
          onClose={() => setConvertModal(false)}
          onConfirm={async () => { setConvertModal(false); await onConvert(estimate.id); }}
        />
      )}
    </div>
  );
}

function EstimateEditModal({ estimate, onClose, onSave }) {
  const [rate, setRate] = useState(estimate.labor_rate ?? 60);
  const [reason, setReason] = useState(estimate.discount_reason || "");
  const [saving, setSaving] = useState(false);
  // Editable line copy — deep-cloned so cancels don't corrupt the parent view.
  const [lines, setLines] = useState(() =>
    (estimate.lines || []).map((l) => ({ ...l }))
  );
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    api.get("/repairs/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);
  const original = Number(estimate.labor_rate ?? 0);
  const rateNum = Number(rate) || 0;
  const newGrand = lines.reduce((sum, l) => {
    const lab = (Number(l.qty) || 1) * (Number(l.labor_hours) || 0) * (Number(l.labor_rate) || rateNum);
    const mat = (Number(l.qty) || 1) * (Number(l.material_cost) || 0);
    return sum + lab + mat;
  }, 0);
  const oldGrand = Number((estimate.totals || {}).grand_total || 0);
  const delta = newGrand - oldGrand;
  const discountPct = original > 0 ? ((original - rateNum) / original) * 100 : 0;

  const updateLine = (i, field, val) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: val } : l)));
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));
  const addBlankLine = () =>
    setLines((prev) => [
      ...prev,
      { item_code: "", description: "", damage_code: "", location: "", qty: 1,
        labor_hours: 0, labor_rate: rateNum, material_cost: 0 },
    ]);
  const addFromCatalog = (item) => {
    if (!item) return;
    setLines((prev) => [
      ...prev,
      {
        item_code: item.item_code,
        description: item.description,
        damage_code: item.damage_code || "",
        location: item.location || "",
        qty: 1,
        labor_hours: Number(item.default_labor_hours) || 0,
        labor_rate: rateNum,
        material_cost: Number(item.material_cost) || 0,
      },
    ]);
  };

  const submit = async () => {
    const n = Number(rate);
    if (!Number.isFinite(n) || n < 0) { toast.error("Labour rate must be a non-negative number"); return; }
    // Guard: reject numeric-only item codes (backend also enforces this)
    const bad = lines.findIndex((l) => l.item_code && /^\d+$/.test(String(l.item_code).trim()));
    if (bad !== -1) { toast.error(`Line ${bad + 1}: item code cannot be a plain number — pick a catalog code.`); return; }
    setSaving(true);
    try {
      await onSave({ labor_rate: n, discount_reason: reason || null, lines });
    } catch { /* toast already shown */ }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="estimate-edit-modal">
      <div className="bg-white border border-gray-200 w-full max-w-4xl shadow-xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500">Edit estimate</div>
            <div className="font-mono font-bold text-lg">{estimate.estimate_no}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-black" data-testid="estimate-edit-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                Labour rate (USD / hr)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="ps-input font-mono text-lg"
                data-testid="estimate-edit-rate"
              />
              <div className="text-[11px] text-gray-500 mt-1">
                Was <span className="font-mono">${original.toFixed(2)}</span> · changing this re-prices unfixed lines.
                {discountPct > 0 && (
                  <span className="ml-2 text-emerald-700 font-bold">{discountPct.toFixed(1)}% discount</span>
                )}
                {discountPct < 0 && (
                  <span className="ml-2 text-red-700 font-bold">+{(-discountPct).toFixed(1)}% surcharge</span>
                )}
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                Reason for change <span className="text-gray-400 normal-case">· optional, recorded on the estimate</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="e.g. Workshop found extra dent — added SHELL-STR-INT"
                className="ps-input"
                data-testid="estimate-edit-reason"
              />
            </div>
          </div>

          {/* Lines editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600">Lines</label>
              <div className="flex items-center gap-2">
                <select
                  className="ps-input h-8 text-xs w-56"
                  defaultValue=""
                  onChange={(e) => {
                    const item = catalog.find((c) => c.item_code === e.target.value);
                    addFromCatalog(item);
                    e.target.value = "";
                  }}
                  data-testid="estimate-edit-catalog-picker"
                >
                  <option value="">+ Add from catalog…</option>
                  {catalog.map((c) => (
                    <option key={c.item_code} value={c.item_code}>{c.item_code} · {c.description}</option>
                  ))}
                </select>
                <button className="ps-btn ps-btn-secondary h-8 text-xs" onClick={addBlankLine} data-testid="estimate-edit-add-blank">
                  <Plus className="w-3.5 h-3.5" /> Blank line
                </button>
              </div>
            </div>
            <div className="border border-gray-200 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-[0.12em] text-gray-600">
                  <tr>
                    <th className="text-left p-2">#</th>
                    <th className="text-left p-2">Item</th>
                    <th className="text-left p-2">Description</th>
                    <th className="text-left p-2">Dam</th>
                    <th className="text-left p-2">Loc</th>
                    <th className="text-right p-2">Qty</th>
                    <th className="text-right p-2">Hrs</th>
                    <th className="text-right p-2">Rate</th>
                    <th className="text-right p-2">Material</th>
                    <th className="text-right p-2">Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan={11} className="text-center py-6 text-gray-400">No lines yet. Add one from the catalog above.</td></tr>
                  )}
                  {lines.map((l, i) => {
                    const lab = (Number(l.qty) || 1) * (Number(l.labor_hours) || 0) * (Number(l.labor_rate) || rateNum);
                    const mat = (Number(l.qty) || 1) * (Number(l.material_cost) || 0);
                    return (
                      <tr key={i} data-testid={`estimate-edit-line-${i}`}>
                        <td className="p-1 text-gray-400 font-mono text-[10px] text-center">{i + 1}</td>
                        <td className="p-1"><input className="ps-input h-7 px-2 font-mono text-xs w-24" value={l.item_code || ""} onChange={(e) => updateLine(i, "item_code", e.target.value)} /></td>
                        <td className="p-1"><input className="ps-input h-7 px-2 text-xs w-full min-w-[16rem]" value={l.description || ""} onChange={(e) => updateLine(i, "description", e.target.value)} /></td>
                        <td className="p-1"><input className="ps-input h-7 px-2 font-mono text-xs w-14 uppercase" value={l.damage_code || ""} onChange={(e) => updateLine(i, "damage_code", e.target.value.toUpperCase())} /></td>
                        <td className="p-1"><input className="ps-input h-7 px-2 font-mono text-xs w-16 uppercase" value={l.location || ""} onChange={(e) => updateLine(i, "location", e.target.value.toUpperCase())} /></td>
                        <td className="p-1"><input type="number" className="ps-input h-7 px-2 text-right font-mono text-xs w-14" value={l.qty ?? 1} onChange={(e) => updateLine(i, "qty", Number(e.target.value))} /></td>
                        <td className="p-1"><input type="number" step="0.1" className="ps-input h-7 px-2 text-right font-mono text-xs w-16" value={l.labor_hours ?? 0} onChange={(e) => updateLine(i, "labor_hours", Number(e.target.value))} /></td>
                        <td className="p-1"><input type="number" step="0.01" className="ps-input h-7 px-2 text-right font-mono text-xs w-20" value={l.labor_rate ?? rateNum} onChange={(e) => updateLine(i, "labor_rate", Number(e.target.value))} /></td>
                        <td className="p-1"><input type="number" step="0.01" className="ps-input h-7 px-2 text-right font-mono text-xs w-20" value={l.material_cost ?? 0} onChange={(e) => updateLine(i, "material_cost", Number(e.target.value))} /></td>
                        <td className="p-1 text-right font-mono font-bold">{fmtMoney(lab + mat)}</td>
                        <td className="p-1"><button className="text-red-600 hover:bg-red-50 p-1" onClick={() => removeLine(i)} data-testid={`estimate-edit-line-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-2 border border-gray-200 divide-x divide-gray-200">
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500">Was</div>
              <div className="font-mono text-xl font-bold">${oldGrand.toFixed(2)}</div>
            </div>
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500">Will be</div>
              <div className={`font-mono text-xl font-bold ${delta < 0 ? "text-emerald-700" : delta > 0 ? "text-red-700" : "text-[#006E9E]"}`}>
                ${newGrand.toFixed(2)}
              </div>
              <div className="text-[10px] mt-0.5 font-mono">
                {delta < 0 ? "−" : delta > 0 ? "+" : ""}${Math.abs(delta).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={onClose} disabled={saving} data-testid="estimate-edit-cancel">Cancel</button>
          <button className="ps-btn ps-btn-primary" onClick={submit} disabled={saving} data-testid="estimate-edit-save">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Customer Work Order modal ───────────────────────────────────────
function WorkOrderModal({ estimate, onClose, onSaved }) {
  const existing = estimate.customer_work_order || {};
  const [woNo, setWoNo] = useState(existing.wo_no || "");
  const [woDate, setWoDate] = useState(existing.wo_date || new Date().toISOString().slice(0, 10));
  const [authBy, setAuthBy] = useState(existing.authorised_by || "");
  const [notes, setNotes] = useState(existing.notes || "");
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [file, setFile] = useState(existing.file || null);

  const submit = async () => {
    if (!woNo.trim()) { toast.error("Work Order number is required"); return; }
    setSaving(true);
    try {
      await api.put(`/repairs/estimates/${estimate.id}/work-order`, {
        wo_no: woNo.trim(),
        wo_date: woDate || null,
        authorised_by: authBy.trim() || null,
        notes: notes.trim() || null,
      });
      toast.success(`Work Order ${woNo.trim()} saved`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const onFile = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/repairs/estimates/${estimate.id}/work-order/file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(data);
      toast.success("Work Order document attached");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingFile(false); ev.target.value = ""; }
  };

  const fileUrl = file?.url
    ? (file.url.startsWith("http") ? file.url : (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + file.url)
    : null;

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="work-order-modal">
      <div className="bg-white border border-gray-200 w-full max-w-lg shadow-xl">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500">Customer Work Order</div>
            <div className="font-mono font-bold text-lg">{estimate.estimate_no}</div>
            <div className="text-xs text-gray-500 mt-0.5">{estimate.customer}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-black" data-testid="work-order-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
              Work Order No <span className="text-red-500">*</span>
            </label>
            <input
              value={woNo}
              onChange={(e) => setWoNo(e.target.value)}
              className="ps-input font-mono"
              placeholder="WO-2026-04567"
              data-testid="work-order-no"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Date received</label>
              <input
                type="date"
                value={woDate}
                onChange={(e) => setWoDate(e.target.value)}
                className="ps-input font-mono"
                data-testid="work-order-date"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Authorised by</label>
              <input
                value={authBy}
                onChange={(e) => setAuthBy(e.target.value)}
                className="ps-input"
                placeholder="Robert Ritchie"
                maxLength={120}
                data-testid="work-order-auth"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Notes <span className="text-gray-400 normal-case">· optional</span></label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              className="ps-input"
              placeholder="e.g. Approved by email 02 Jun 2026"
              data-testid="work-order-notes"
            />
          </div>
          <div className="border border-dashed border-gray-300 p-3 bg-gray-50">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-600 mb-1.5">WO document <span className="text-gray-400 normal-case">· PDF or image, optional</span></div>
            {file ? (
              <div className="flex items-center justify-between gap-2 text-xs">
                <a href={fileUrl} target="_blank" rel="noreferrer" className="text-blue-700 underline break-all" data-testid="work-order-file-attached">
                  {file.original_name}
                </a>
                <label className="ps-btn ps-btn-secondary h-7 px-2 text-xs cursor-pointer">
                  Replace
                  <input type="file" accept=".pdf,image/*" className="hidden" onChange={onFile} disabled={uploadingFile} />
                </label>
              </div>
            ) : (
              <label className="ps-btn ps-btn-secondary cursor-pointer" data-testid="work-order-file-upload">
                {uploadingFile ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Attach file</>}
                <input type="file" accept=".pdf,image/*" className="hidden" onChange={onFile} disabled={uploadingFile} />
              </label>
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={onClose} disabled={saving} data-testid="work-order-cancel">Cancel</button>
          <button className="ps-btn ps-btn-primary" onClick={submit} disabled={saving} data-testid="work-order-save">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save</>}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────── Convert-to-invoice confirm ───────────────────────────────────────
function ConvertConfirmModal({ estimate, onClose, onConfirm }) {
  const cwo = estimate.customer_work_order || {};
  const total = estimate.totals?.grand_total ?? 0;
  const [confirming, setConfirming] = useState(false);
  const go = async () => {
    setConfirming(true);
    try { await onConfirm(); } finally { setConfirming(false); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="convert-confirm-modal">
      <div className="bg-white border border-gray-200 w-full max-w-md shadow-xl">
        <div className="px-5 py-4 border-b border-gray-200">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500">Generate Invoice</div>
          <div className="font-mono font-bold text-lg">{estimate.estimate_no}</div>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div>The repair is COMPLETED. We'll generate a customer invoice and lock this estimate as INVOICED.</div>
          <div className="border border-emerald-200 bg-emerald-50 p-3 text-xs space-y-1">
            <div><span className="font-bold uppercase tracking-wider text-emerald-900">Customer WO:</span> <span className="font-mono font-bold ml-2">{cwo.wo_no}</span></div>
            {cwo.wo_date && <div><span className="font-bold uppercase tracking-wider text-emerald-900">Dated:</span> <span className="ml-2">{new Date(cwo.wo_date).toLocaleDateString()}</span></div>}
            {cwo.authorised_by && <div><span className="font-bold uppercase tracking-wider text-emerald-900">By:</span> <span className="ml-2">{cwo.authorised_by}</span></div>}
          </div>
          <div className="grid grid-cols-2 border border-gray-200 divide-x divide-gray-200">
            <div className="p-3"><div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Container</div><div className="font-mono">{estimate.container_number}</div></div>
            <div className="p-3"><div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Amount</div><div className="font-mono font-bold text-[#006E9E]">{estimate.currency} {Number(total).toFixed(2)}</div></div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={onClose} disabled={confirming} data-testid="convert-cancel">Cancel</button>
          <button className="ps-btn ps-btn-primary" onClick={go} disabled={confirming} data-testid="convert-confirm">
            {confirming ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><FileText className="w-4 h-4" /> Generate Invoice</>}
          </button>
        </div>
      </div>
    </div>
  );
}



// ─────────────────────────────────────── Repair completion photo gallery ───────────────────────────────────────
function EstimatePhotosSection({ estimate, canEdit, isAdmin = false }) {
  const [photos, setPhotos] = useState(estimate.photos || []);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [lightbox, setLightbox] = useState(null);

  const canAttach = canEdit && ["IN_PROGRESS", "COMPLETED", "INVOICED"].includes(estimate.status);
  // Photos can still be added on INVOICED estimates by an admin (late
  // documentation amendment — the backend permits it and the action is audit-
  // logged). Non-admins see the "locked" message instead.
  const lockedInvoiced = estimate.status === "INVOICED" && !isAdmin;

  useEffect(() => { setPhotos(estimate.photos || []); }, [estimate.id, estimate.photos]);

  const onFileSelect = async (ev) => {
    const files = Array.from(ev.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        if (caption.trim()) fd.append("caption", caption.trim());
        const { data } = await api.post(`/repairs/estimates/${estimate.id}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) => [...p, data]);
      }
      toast.success(`Uploaded ${files.length} photo${files.length === 1 ? "" : "s"}`);
      setCaption("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); ev.target.value = ""; }
  };

  const remove = async (photoId) => {
    if (!window.confirm("Remove this photo from the repair record?")) return;
    try {
      await api.delete(`/repairs/estimates/${estimate.id}/photos/${photoId}`);
      setPhotos((p) => p.filter((x) => x.id !== photoId));
      toast.success("Photo removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!canAttach && photos.length === 0) return null;

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const photoUrl = (p) => (p.url || "").startsWith("http") ? p.url : `${backendBase}${p.url}`;

  return (
    <div className="border border-gray-200 bg-white" data-testid="estimate-photos">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3 bg-gray-50">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4 text-[#006E9E]" />
          <div>
            <div className="text-sm font-bold uppercase tracking-[0.14em]">Repair photos</div>
            <div className="text-[11px] text-gray-500">
              {photos.length} photo{photos.length === 1 ? "" : "s"} on file
              {lockedInvoiced && " · invoiced, photos locked"}
            </div>
          </div>
        </div>
        {canAttach && !lockedInvoiced && (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Optional caption for next upload"
              maxLength={120}
              className="ps-input h-8 text-xs w-56"
              data-testid="estimate-photo-caption"
            />
            <label className="ps-btn ps-btn-primary cursor-pointer" data-testid="estimate-photo-upload">
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Add photos</>}
              <input
                type="file"
                multiple
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={onFileSelect}
                disabled={uploading}
              />
            </label>
          </div>
        )}
      </div>

      {photos.length === 0 ? (
        <div className="p-8 text-center text-gray-500 text-sm">
          <ImageIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          No photos yet. Snap before/after shots, weld close-ups, sealed manlid, etc.
        </div>
      ) : (
        <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p) => {
            // Photos migrated to Google Drive on 25-Jun-2026 carry a
            // `drive_file_id`. Anything without one is a legacy photo whose
            // bytes were wiped when the ephemeral container disk got cleared
            // during a deploy — no way to recover, must be re-uploaded.
            const isLegacy = !p.drive_file_id;
            return (
              <div key={p.id} className="relative group border border-gray-200 bg-gray-50 overflow-hidden" data-testid={`estimate-photo-${p.id}`}>
                <button
                  type="button"
                  onClick={() => !isLegacy && setLightbox(p)}
                  disabled={isLegacy}
                  className={`block w-full aspect-square overflow-hidden ${isLegacy ? "cursor-not-allowed" : ""}`}
                  title={isLegacy ? "Legacy photo (pre-Drive migration) — bytes lost, please re-upload" : undefined}
                >
                  {isLegacy ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-amber-50 border border-amber-200 text-amber-800 p-3 text-center gap-1">
                      <AlertCircle className="w-6 h-6" />
                      <div className="text-[10px] font-bold uppercase tracking-[0.14em]">Re-upload needed</div>
                      <div className="text-[10px] text-amber-700">Legacy — bytes lost</div>
                    </div>
                  ) : (
                    <img
                      src={photoUrl(p)}
                      alt={p.caption || p.original_name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                      onError={(e) => {
                        // Fallback if Drive returned an error / photo pruned /
                        // network hiccup — swap the broken img icon for a
                        // clean placeholder so operators know to re-upload
                        // rather than think the UI is buggy.
                        const wrap = e.currentTarget.parentElement;
                        if (wrap && !wrap.querySelector("[data-photo-fallback]")) {
                          e.currentTarget.style.display = "none";
                          const div = document.createElement("div");
                          div.setAttribute("data-photo-fallback", "1");
                          div.className = "w-full h-full flex flex-col items-center justify-center bg-red-50 border border-red-200 text-red-700 p-3 text-center gap-1";
                          div.innerHTML = `<div class="text-[10px] font-bold uppercase tracking-[0.14em]">Photo unavailable</div><div class="text-[10px] text-red-600">Try re-upload</div>`;
                          wrap.appendChild(div);
                        }
                      }}
                    />
                  )}
                </button>
                <div className="px-2 py-1.5 text-[10px] text-gray-600 truncate" title={p.caption || p.original_name}>
                  {p.caption || p.original_name}
                </div>
                <div className="px-2 pb-1.5 text-[9px] font-mono text-gray-400">
                  {(p.uploaded_by || "").split("@")[0]} · {new Date(p.uploaded_at).toLocaleDateString()}
                </div>
                {canAttach && !lockedInvoiced && (
                  <button
                    onClick={() => remove(p.id)}
                    className="absolute top-1 right-1 bg-white/90 hover:bg-red-50 border border-gray-200 text-red-600 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    title={isLegacy ? "Remove legacy photo entry" : "Remove photo"}
                    data-testid={`estimate-photo-delete-${p.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="estimate-photo-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            onClick={() => setLightbox(null)}
          >
            <X className="w-7 h-7" />
          </button>
          <div className="max-w-5xl max-h-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img src={photoUrl(lightbox)} alt={lightbox.caption || lightbox.original_name} className="max-h-[80vh] object-contain" />
            <div className="text-white text-sm text-center">
              {lightbox.caption && <div className="font-medium">{lightbox.caption}</div>}
              <div className="text-white/60 text-xs mt-1 font-mono">
                {lightbox.original_name} · {lightbox.uploaded_by} · {new Date(lightbox.uploaded_at).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function InspectionsTab({ canEdit, canDelete }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/repairs/inspections");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (insp_id, no) => {
    if (!window.confirm(`Delete inspection ${no}?`)) return;
    try {
      await api.delete(`/repairs/inspections/${insp_id}`);
      toast.success(`Deleted ${no}`);
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (creating) {
    return <InspectionForm onCancel={() => setCreating(false)} onSaved={(d) => { setCreating(false); load(); setSelected(d); }} />;
  }
  if (selected) {
    return <InspectionDetail
      inspection={selected}
      canEdit={canEdit}
      canDelete={canDelete}
      onBack={() => setSelected(null)}
      onDelete={remove}
      onChanged={async () => {
        const { data } = await api.get(`/repairs/inspections/${selected.id}`);
        setSelected(data);
      }}
    />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-gray-500 font-mono">{items.length} inspection{items.length === 1 ? "" : "s"}</div>
        <div className="flex gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</button>
          {canEdit && <button className="ps-btn ps-btn-primary" onClick={() => setCreating(true)} data-testid="inspection-new"><Plus className="w-4 h-4" /> New Inspection</button>}
        </div>
      </div>
      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="ps-table" data-testid="inspections-table">
          <thead>
            <tr><th>No.</th><th>Type</th><th>Container</th><th>Customer</th><th>Surveyor</th><th>Survey Date</th><th className="text-right">Damages</th><th></th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-10 text-gray-400">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-gray-400">No inspections yet.</td></tr>
            )}
            {!loading && items.map((i) => (
              <tr key={i.id} data-testid={`inspection-row-${i.inspection_no}`}>
                <td className="font-mono font-bold">{i.inspection_no}</td>
                <td><span className={`ps-pill ${i.inspection_type === "ONHIRE" ? "ps-pill-success" : "ps-pill-warn"}`}><span className="dot" /> {i.inspection_type}</span></td>
                <td className="font-mono">{i.container_number}</td>
                <td className="text-xs">{i.customer || "—"}</td>
                <td className="text-xs">{i.surveyor || "—"}</td>
                <td className="text-xs font-mono text-gray-500">{fmtDate(i.survey_date)}</td>
                <td className="text-right font-mono">{(i.damages || []).length}</td>
                <td className="text-right">
                  <button className="ps-btn ps-btn-secondary h-7 px-3 text-xs" onClick={() => setSelected(i)} data-testid={`open-inspection-${i.inspection_no}`}>
                    <Eye className="w-3.5 h-3.5" /> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InspectionForm({ onCancel, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    container_number: "", inspection_type: "ONHIRE", survey_date: today,
    surveyor: "", customer: "", depot: "Tema, Ghana",
    iso_type: "", manufacturer: "", manu_date: "", manu_serial: "", csc_due: "",
    tare_weight_kg: "", max_gross_weight_kg: "", capacity_liters: "", mawp_shell_bar: "", mawp_heating_bar: "",
    global_remarks: "", damages: [],
  });
  const [damageForm, setDamageForm] = useState({ damage_code: "", location: "", description: "" });
  const [saving, setSaving] = useState(false);
  // Partner list for the Customer datalist — keeps counterparty names
  // consistent with the rest of the app.
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    api.get("/partners?fields=code,legal_name")
      .then((r) => setPartners(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPartners([]));
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addDamage = () => {
    if (!damageForm.description.trim() && !damageForm.damage_code) { toast.error("Add a code or description"); return; }
    setForm((f) => ({ ...f, damages: [...f.damages, damageForm] }));
    setDamageForm({ damage_code: "", location: "", description: "" });
  };
  const removeDamage = (i) => setForm((f) => ({ ...f, damages: f.damages.filter((_, x) => x !== i) }));

  const save = async () => {
    if (!form.container_number.trim()) { toast.error("Container required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, container_number: form.container_number.trim().toUpperCase() };
      ["tare_weight_kg","max_gross_weight_kg","capacity_liters","mawp_shell_bar","mawp_heating_bar"].forEach((k) => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      payload.survey_date = new Date(form.survey_date).toISOString();
      const { data } = await api.post("/repairs/inspections", payload);
      toast.success(`Inspection ${data.inspection_no} created`);
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="inspection-form">
      <div className="flex items-center justify-between">
        <button className="ps-btn ps-btn-secondary" onClick={onCancel}><ArrowLeft className="w-4 h-4" /> Back</button>
        <button className="ps-btn ps-btn-primary" onClick={save} disabled={saving} data-testid="inspection-save">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save Inspection</>}
        </button>
      </div>

      <div className="border border-gray-200 bg-white p-5 grid sm:grid-cols-3 gap-3">
        {[
          ["container_number", "Container No *", "text"],
          ["inspection_type", "Type", "select", ["ONHIRE", "OFFHIRE"]],
          ["survey_date", "Survey Date", "date"],
          ["surveyor", "Surveyor", "text"],
          ["customer", "Customer", "text"],
          ["depot", "Depot", "text"],
          ["iso_type", "ISO Type", "text"],
          ["manufacturer", "Manufacturer", "text"],
          ["manu_date", "Manu Date (MM/YYYY)", "text"],
          ["manu_serial", "Manu Serial No", "text"],
          ["csc_due", "CSC Due (MM/YYYY)", "text"],
          ["tare_weight_kg", "Tare (kg)", "number"],
          ["max_gross_weight_kg", "Max Gross (kg)", "number"],
          ["capacity_liters", "Capacity (L)", "number"],
          ["mawp_shell_bar", "MAWP Shell (bar)", "number"],
          ["mawp_heating_bar", "MAWP Heating (bar)", "number"],
        ].map(([k, l, t, opts]) => (
          <div key={k}>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">{l}</label>
            {t === "select" ? (
              <select value={form[k]} onChange={(e) => upd(k, e.target.value)} className="ps-input font-mono" data-testid={`inspection-${k}`}>
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : k === "customer" ? (
              <select
                value={partners.find((p) => (p.legal_name || p.code) === form[k])?.code || ""}
                onChange={(e) => {
                  const code = e.target.value;
                  const partner = partners.find((p) => p.code === code);
                  upd(k, partner ? (partner.legal_name || partner.code) : "");
                }}
                className="ps-input"
                data-testid={`inspection-${k}`}
              >
                <option value="">— Select customer —</option>
                {partners.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.code}{p.legal_name && p.legal_name !== p.code ? ` · ${p.legal_name}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input type={t} value={form[k]} onChange={(e) => upd(k, e.target.value)} className="ps-input font-mono" data-testid={`inspection-${k}`} />
            )}
          </div>
        ))}
        <div className="sm:col-span-3">
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Global Remarks</label>
          <textarea value={form.global_remarks} onChange={(e) => upd("global_remarks", e.target.value)} rows={3} className="ps-input" data-testid="inspection-remarks" />
        </div>
      </div>

      <div className="border border-gray-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-700 mb-3">Damages ({form.damages.length})</div>
        <div className="grid sm:grid-cols-4 gap-2 mb-3 items-end">
          <input placeholder="Dam (TS/LO/HO…)" maxLength={3} value={damageForm.damage_code} onChange={(e) => setDamageForm({ ...damageForm, damage_code: e.target.value.toUpperCase() })} className="ps-input font-mono" data-testid="damage-code" />
          <input placeholder="Loc (TOP/BOT/RHS…)" value={damageForm.location} onChange={(e) => setDamageForm({ ...damageForm, location: e.target.value.toUpperCase() })} className="ps-input font-mono" data-testid="damage-loc" />
          <input placeholder="Description" value={damageForm.description} onChange={(e) => setDamageForm({ ...damageForm, description: e.target.value })} className="ps-input" data-testid="damage-desc" />
          <button className="ps-btn ps-btn-primary" onClick={addDamage} data-testid="damage-add"><Plus className="w-4 h-4" /> Add</button>
        </div>
        {form.damages.length > 0 && (
          <ul className="text-xs space-y-1">
            {form.damages.map((d, i) => (
              <li key={i} className="flex items-center gap-2 font-mono py-1 px-2 bg-gray-50">
                <span className="font-bold">[{d.damage_code || "?"}/{d.location || "?"}]</span>
                <span className="flex-1">{d.description}</span>
                <button onClick={() => removeDamage(i)} className="text-red-600"><X className="w-3.5 h-3.5" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function InspectionDetail({ inspection, canEdit, canDelete, onBack, onDelete, onChanged }) {
  const fileRefs = useRef({});
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const uploadPhoto = async (damageIdx, e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingIdx(damageIdx);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("damage_index", String(damageIdx));
        fd.append("file", f);
        await api.post(`/repairs/inspections/${inspection.id}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} photo(s) attached`);
      e.target.value = "";
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingIdx(null); }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/repairs/inspections/${inspection.id}/photos/${photoId}`);
      toast.success("Photo deleted");
      setLightbox(null);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="inspection-detail">
      <div className="flex items-center justify-between">
        <button className="ps-btn ps-btn-secondary" onClick={onBack}><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="flex gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={() => downloadInspectionPdf(inspection)} data-testid="inspection-pdf">
            <Printer className="w-4 h-4" /> Print PDF
          </button>
          {canDelete && (
            <button className="ps-btn ps-btn-secondary text-red-600" onClick={() => onDelete(inspection.id, inspection.inspection_no)}>
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="border border-gray-200 bg-white p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Inspection</div>
        <div className="font-mono font-bold text-lg">{inspection.inspection_no} · {inspection.inspection_type}</div>
        <div className="text-xs text-gray-500 mt-1">{inspection.container_number} · surveyed {fmtDate(inspection.survey_date)} by {inspection.surveyor || "—"}</div>
        <div className="mt-4 grid sm:grid-cols-4 gap-3 text-xs">
          {[
            ["Customer", inspection.customer], ["Depot", inspection.depot], ["ISO Type", inspection.iso_type],
            ["Manufacturer", inspection.manufacturer], ["Manu Date", inspection.manu_date], ["CSC Due", inspection.csc_due],
            ["Tare (kg)", inspection.tare_weight_kg], ["Max Gross (kg)", inspection.max_gross_weight_kg],
            ["Capacity (L)", inspection.capacity_liters], ["MAWP Shell", inspection.mawp_shell_bar],
          ].map(([l, v]) => (
            <div key={l}><div className="text-[9px] uppercase tracking-[0.15em] font-bold text-gray-500">{l}</div><div className="font-mono">{v || "—"}</div></div>
          ))}
        </div>
        {inspection.global_remarks && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500 mb-1">Remarks</div>
            <div className="text-sm">{inspection.global_remarks}</div>
          </div>
        )}
      </div>

      {(inspection.damages || []).length > 0 && (
        <div className="border border-gray-200 bg-white p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-700 mb-3">
            Damages ({inspection.damages.length})
          </div>
          <div className="space-y-3">
            {inspection.damages.map((d, i) => (
              <div key={i} className="border border-gray-100 p-3 bg-gray-50/50" data-testid={`damage-block-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-xs font-mono">
                      <span className="font-bold px-2 py-0.5 bg-red-100 text-red-800 rounded">
                        {d.damage_code || "?"}
                      </span>
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                        {d.location || "?"}
                      </span>
                      <span className="text-gray-400">#{i + 1}</span>
                    </div>
                    <div className="text-sm mt-2">{d.description || <em className="text-gray-400">no description</em>}</div>
                  </div>
                  {canEdit && (
                    <div>
                      <input
                        ref={(el) => { fileRefs.current[i] = el; }}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple
                        className="hidden"
                        onChange={(e) => uploadPhoto(i, e)}
                        data-testid={`photo-input-${i}`}
                      />
                      <button
                        className="ps-btn ps-btn-secondary h-8 text-xs"
                        onClick={() => fileRefs.current[i]?.click()}
                        disabled={uploadingIdx === i}
                        data-testid={`photo-upload-${i}`}
                      >
                        {uploadingIdx === i
                          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
                          : <><Camera className="w-3.5 h-3.5" /> Add photo</>}
                      </button>
                    </div>
                  )}
                </div>
                {(d.photos || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {d.photos.map((p) => (
                      <div key={p.id} className="relative group">
                        <img
                          src={p.url}
                          alt={p.original_name || ""}
                          className="w-24 h-24 object-cover border border-gray-200 cursor-pointer hover:opacity-80"
                          onClick={() => setLightbox(p)}
                          data-testid={`photo-${p.id}`}
                        />
                        {canEdit && (
                          <button
                            className="absolute -top-1 -right-1 bg-white border border-gray-300 rounded-full p-1 opacity-0 group-hover:opacity-100 hover:bg-red-50"
                            onClick={() => deletePhoto(p.id)}
                          >
                            <X className="w-3 h-3 text-red-600" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 cursor-pointer"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox.url} alt="" className="max-w-[90vw] max-h-[90vh] object-contain" />
          <button className="absolute top-4 right-4 text-white p-2 bg-white/10 rounded-full" onClick={() => setLightbox(null)}>
            <X className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
}



// ─────────────────────────────────────── PARTS ON ORDER TAB ───────────────────────────────────────
const PARTS_STATUS_COLORS = {
  DRAFT:      "ps-pill-warn",
  ORDERED:    "ps-pill-info",
  IN_TRANSIT: "ps-pill-info",
  DELIVERED:  "ps-pill-success",
  CANCELLED:  "ps-pill-danger",
};

function PartsOrdersTab({ canEdit, canDelete }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/repairs/parts-orders${q}`);
      setOrders(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const counts = orders.reduce((acc, o) => {
    acc.total += 1;
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, { total: 0 });

  if (selected) {
    return (
      <PartsOrderDetail
        order={selected}
        canEdit={canEdit}
        canDelete={canDelete}
        onBack={() => setSelected(null)}
        onChanged={() => { load(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="parts-orders-tab">
      <div className="border border-blue-100 bg-blue-50/50 p-4 text-xs text-gray-700">
        <strong className="block uppercase tracking-[0.16em] text-[#006E9E] mb-1">Parts on order · vendor tracking</strong>
        Every Van Doorn PDF you upload on the Rate Card automatically creates an <b>ORDERED</b> entry here. Set the ETA, mark items as received, and the order auto-advances to DELIVERED.
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3" data-testid="parts-orders-kpi">
        {[
          { label: "Total", v: counts.total },
          { label: "Ordered", v: counts.ORDERED || 0 },
          { label: "In transit", v: counts.IN_TRANSIT || 0 },
          { label: "Delivered", v: counts.DELIVERED || 0 },
          { label: "Cancelled", v: counts.CANCELLED || 0 },
          { label: "Draft", v: counts.DRAFT || 0 },
        ].map((k) => (
          <div key={k.label} className="border border-gray-200 bg-white px-3 py-2.5">
            <div className="text-[9px] uppercase tracking-[0.16em] text-gray-500 font-bold">{k.label}</div>
            <div className="text-xl font-bold text-[#1A1410] mt-0.5 font-mono">{k.v}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 items-end">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Filter status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="ps-input font-mono" data-testid="parts-orders-status-filter">
            <option value="">All</option>
            <option value="DRAFT">Draft</option>
            <option value="ORDERED">Ordered</option>
            <option value="IN_TRANSIT">In transit</option>
            <option value="DELIVERED">Delivered</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
        <button className="ps-btn ps-btn-secondary" onClick={load} data-testid="parts-orders-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="ps-table" data-testid="parts-orders-table">
          <thead>
            <tr>
              <th>Order #</th>
              <th>Vendor</th>
              <th>Vendor offer</th>
              <th>Order date</th>
              <th>ETA</th>
              <th className="text-right">Lines</th>
              <th className="text-right">Total</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-10 text-gray-400">Loading…</td></tr>}
            {!loading && orders.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-gray-400">
                No parts orders yet. Upload a Van Doorn PDF on the Rate Card tab to create one.
              </td></tr>
            )}
            {!loading && orders.map((o) => (
              <tr key={o.id} data-testid={`parts-order-row-${o.order_no}`}>
                <td className="font-mono font-bold">{o.order_no}</td>
                <td className="font-mono text-xs">{o.vendor}</td>
                <td className="font-mono text-xs text-gray-600">{o.vendor_offer_no || "—"}</td>
                <td className="font-mono text-xs">{o.order_date || "—"}</td>
                <td className="font-mono text-xs">{o.eta_date || "—"}</td>
                <td className="text-right font-mono">{(o.lines || []).length}</td>
                <td className="text-right font-mono font-bold">{fmtMoney(o.subtotal, o.currency)}</td>
                <td><span className={`ps-pill ${PARTS_STATUS_COLORS[o.status] || ""}`}>{o.status}</span></td>
                <td className="text-right">
                  <button className="ps-btn ps-btn-secondary h-7 px-3 text-xs" onClick={() => setSelected(o)} data-testid={`open-parts-order-${o.order_no}`}>
                    <Eye className="w-3 h-3" /> Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PartsOrderDetail({ order, canEdit, canDelete, onBack, onChanged }) {
  const [doc, setDoc] = useState(order);
  const [saving, setSaving] = useState(false);
  const [received, setReceived] = useState(
    Object.fromEntries((order.lines || []).map((ln) => [ln.item_code, ln.received_qty || 0])),
  );

  const patch = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/repairs/parts-orders/${doc.id}`, payload);
      setDoc(data);
      toast.success("Updated");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveReceived = () => patch({ received_qty_by_line: received });

  const setStatus = (status) => patch({ status });

  const remove = async () => {
    if (!window.confirm(`Delete order ${doc.order_no}?`)) return;
    try {
      await api.delete(`/repairs/parts-orders/${doc.id}`);
      toast.success("Deleted");
      onChanged?.();
      onBack();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="parts-order-detail">
      <div className="flex items-center justify-between">
        <button className="ps-btn ps-btn-secondary" onClick={onBack} data-testid="parts-order-back">
          <ArrowLeft className="w-4 h-4" /> Back to orders
        </button>
        <div className="flex items-center gap-2">
          <span className={`ps-pill ${PARTS_STATUS_COLORS[doc.status] || ""}`}>{doc.status}</span>
          {canDelete && (
            <button className="ps-btn ps-btn-secondary text-red-600" onClick={remove} data-testid="parts-order-delete">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="border border-gray-200 bg-white p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500">Order</div>
        <div className="text-xl font-bold mt-0.5 font-mono">{doc.order_no}</div>
        <div className="grid sm:grid-cols-4 gap-3 mt-4">
          <KV k="Vendor" v={doc.vendor} />
          <KV k="Vendor offer" v={doc.vendor_offer_no || "—"} />
          <KV k="Order date" v={doc.order_date || "—"} />
          <KV k="Currency" v={doc.currency} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">ETA (delivery)</label>
            <input type="date" value={doc.eta_date || ""}
                   onChange={(e) => setDoc({ ...doc, eta_date: e.target.value })}
                   onBlur={() => doc.eta_date !== order.eta_date && patch({ eta_date: doc.eta_date })}
                   disabled={!canEdit || saving}
                   className="ps-input font-mono" data-testid="parts-order-eta" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Notes</label>
            <input value={doc.notes || ""}
                   onChange={(e) => setDoc({ ...doc, notes: e.target.value })}
                   onBlur={() => doc.notes !== order.notes && patch({ notes: doc.notes })}
                   disabled={!canEdit || saving}
                   placeholder="e.g. shipped via DSV, expected at MTG"
                   className="ps-input" data-testid="parts-order-notes" />
          </div>
        </div>

        {canEdit && (
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
            {doc.status === "ORDERED" && (
              <button className="ps-btn ps-btn-secondary" onClick={() => setStatus("IN_TRANSIT")} disabled={saving} data-testid="status-in-transit">
                Mark In Transit
              </button>
            )}
            {(doc.status === "ORDERED" || doc.status === "IN_TRANSIT") && (
              <button className="ps-btn ps-btn-primary" onClick={() => setStatus("DELIVERED")} disabled={saving} data-testid="status-delivered">
                Mark All Delivered
              </button>
            )}
            {doc.status !== "CANCELLED" && doc.status !== "DELIVERED" && (
              <button className="ps-btn ps-btn-secondary text-red-600" onClick={() => setStatus("CANCELLED")} disabled={saving} data-testid="status-cancel">
                Cancel order
              </button>
            )}
          </div>
        )}
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="ps-table" data-testid="parts-order-lines">
          <thead>
            <tr>
              <th>Item</th>
              <th>Description</th>
              <th>Unit</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Unit price</th>
              <th className="text-right">Line total</th>
              <th className="text-right">Received</th>
            </tr>
          </thead>
          <tbody>
            {(doc.lines || []).map((ln) => (
              <tr key={ln.item_code} data-testid={`parts-order-line-${ln.item_code}`}>
                <td className="font-mono text-xs font-bold">{ln.item_code}</td>
                <td className="text-xs">{ln.description}</td>
                <td className="font-mono text-xs">{ln.unit || "—"}</td>
                <td className="text-right font-mono">{Number(ln.qty || 0).toFixed(2)}</td>
                <td className="text-right font-mono">{fmtMoney(ln.unit_price, doc.currency)}</td>
                <td className="text-right font-mono font-bold">{fmtMoney(ln.line_total, doc.currency)}</td>
                <td className="text-right">
                  <input
                    type="number" step="0.01" min={0} max={ln.qty}
                    value={received[ln.item_code] ?? 0}
                    onChange={(e) => setReceived({ ...received, [ln.item_code]: Number(e.target.value) })}
                    disabled={!canEdit || saving}
                    className="ps-input font-mono w-24 text-right"
                    data-testid={`received-${ln.item_code}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} className="text-right font-bold uppercase tracking-[0.14em] text-[10px] text-gray-500">Order total</td>
              <td className="text-right font-mono font-bold">{fmtMoney(doc.subtotal, doc.currency)}</td>
              <td className="text-right">
                {canEdit && (
                  <button className="ps-btn ps-btn-primary h-7 px-3 text-xs" onClick={saveReceived} disabled={saving} data-testid="save-received">
                    <Save className="w-3 h-3" /> Save received
                  </button>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.18em] text-gray-500 font-bold">{k}</div>
      <div className="text-sm font-mono mt-0.5">{v}</div>
    </div>
  );
}


// ─────────────────────────────────────── MAIN PAGE ───────────────────────────────────────
export default function Repairs() {
  const { user } = useAuth();
  // Role gate aligned with backend ROLES_REPAIRS_MANAGE (admin, supervisor,
  // general_manager, depot_manager). Finance is added below as canInvoice
  // because they own the final billing step.
  const MANAGE_ROLES = ["admin", "supervisor", "general_manager", "depot_manager"];
  const canEdit = MANAGE_ROLES.includes(user?.role);
  // Generate-invoice can be done by anyone in MANAGE plus finance.
  const canInvoice = canEdit || user?.role === "finance";
  const canDelete = user?.role === "admin";
  const [tab, setTab] = useState("estimates");

  return (
    <div data-testid="repairs-page">
      <PageHeader
        title="Tank Repairs"
        subtitle="Onhire / offhire inspections, repair estimates, and the depot rate card."
        testid="repairs-header"
      />
      <div className="px-6 sm:px-8 pt-4 border-b border-gray-200 bg-white flex gap-1">
        {[
          { v: "estimates", l: "Estimates", i: FileText },
          { v: "inspections", l: "Inspections", i: ClipboardCheck },
          { v: "parts-orders", l: "Parts on Order", i: Wrench },
          { v: "catalog", l: "Rate Card", i: BookOpen },
        ].map((t) => {
          const Icon = t.i;
          return (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={`tab-${t.v}`}
              className={`px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition-colors inline-flex items-center gap-2 ${
                tab === t.v ? "border-[#006E9E] text-[#006E9E]" : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.l}
            </button>
          );
        })}
      </div>
      <div className="p-6 sm:p-8">
        {tab === "estimates" && <EstimatesTab canEdit={canEdit} canDelete={canDelete} canInvoice={canInvoice} />}
        {tab === "inspections" && <InspectionsTab canEdit={canEdit} canDelete={canDelete} />}
        {tab === "parts-orders" && <PartsOrdersTab canEdit={canEdit} canDelete={canDelete} />}
        {tab === "catalog" && <CatalogTab canEdit={canEdit} canDelete={canDelete} />}
      </div>
    </div>
  );
}
